import { useEffect, useRef, useState } from "react";
import {
	Animated,
	type LayoutChangeEvent,
	StyleSheet,
	View,
} from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { Text, useTheme } from "react-native-paper";
import { interpolateIntegerValue } from "./helpers";

type CenteredSliderProps = {
	max: number;
	disabled?: boolean;
	value?: number;
	onValueChange: (value: number) => void;
};

const THUMB_SIZE = 44;
const TRACK_HEIGHT = 16;

export const CenteredSlider = ({
	max,
	value = 0,
	disabled = false,
	onValueChange,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: CenteredSliderProps) => {
	const { colors } = useTheme();
	const { current: position } = useRef(new Animated.Value(0));
	const [sliderWidth, setSliderWidth] = useState(0);
	const [currentPosition, setCurrentPosition] = useState(0);
	const [sliderValue, setSliderValue] = useState<number | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const handleLayout = (event: LayoutChangeEvent) => {
		const { width } = event.nativeEvent.layout;
		setSliderWidth(width);
	};

	const min = -max;

	useEffect(() => {
		if (value !== null) {
			const pos = interpolateIntegerValue(
				value ?? 0,
				[min, max],
				[0, sliderWidth],
			);
			position.setValue(pos);
			setCurrentPosition(pos);
			setSliderValue(value ?? 0);
		}
	}, [value, min, max, sliderWidth]);

	const panGesture = Gesture.Pan()
		.onBegin(() => {
			setIsDragging(true);
			position.addListener(({ value }) => {
				const ratio = value / sliderWidth;
				const interpolatedValue = Math.round(min + ratio * (max - min));
				const val = Math.min(Math.max(interpolatedValue, min), max);
				setSliderValue(val);
			});
		})
		.onUpdate((e) => {
			const newPosition = Math.max(
				0,
				Math.min(e.translationX + currentPosition, sliderWidth),
			);
			position.setValue(newPosition);
		})
		.onEnd((e) => {
			setCurrentPosition(e.translationX + currentPosition);
			setIsDragging(false);
			onValueChange(sliderValue ?? 0);
		})
		.runOnJS(true);

	return (
		<View
			pointerEvents={disabled ? "none" : "auto"}
			style={[styles.track]}
			onLayout={handleLayout}
		>
			<Animated.View
				style={[
					styles.trackActive,
					{
						width: position.interpolate({
							inputRange: [0, sliderWidth],
							outputRange: ["0%", "100%"],
						}),
						backgroundColor: disabled
							? colors.onSurface
							: colors.secondaryContainer,
						opacity: disabled ? 0.38 : 1,
					},
				]}
			>
				<View
					style={{
						left: 6,
						width: 4,
						height: 4,
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
						borderRadius: 2,
					}}
				/>
				<View
					style={{
						left: sliderWidth / 2,
						width: 4,
						height: 4,
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
						borderRadius: 2,
					}}
				/>
			</Animated.View>
			<GestureDetector gesture={panGesture}>
				<Animated.View
					style={[styles.thumb, { transform: [{ translateX: position }] }]}
				>
					<View
						style={{
							width: isDragging ? 2 : 4,
							height: "100%",
							backgroundColor: disabled ? colors.onSurface : colors.primary,
							opacity: disabled ? 0.38 : 1,
							borderRadius: 4,
						}}
					/>
					{isDragging && (
						<Text
							style={[
								styles.valueLabel,
								{
									opacity: disabled ? 0.12 : 1,
									backgroundColor: colors.inverseSurface,
									color: colors.inverseOnSurface,
								},
							]}
						>
							{sliderValue}
						</Text>
					)}
				</Animated.View>
			</GestureDetector>
			<Animated.View
				style={[
					styles.trackInactive,
					{
						width: position.interpolate({
							inputRange: [0, sliderWidth],
							outputRange: ["100%", "0%"],
						}),
						backgroundColor: disabled
							? colors.onSurface
							: colors.secondaryContainer,
						opacity: disabled ? 0.12 : 1,
					},
				]}
			>
				<View
					style={{
						right: sliderWidth / 2,
						width: 4,
						height: 4,
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
						borderRadius: 2,
					}}
				/>
				<View
					style={{
						right: 6,
						width: 4,
						height: 4,
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
						borderRadius: 2,
					}}
				/>
			</Animated.View>
		</View>
	);
};

const styles = StyleSheet.create({
	track: {
		width: "100%",
		flexDirection: "row",
		height: TRACK_HEIGHT,
		borderRadius: TRACK_HEIGHT / 2,
		gap: 16,
		marginHorizontal: THUMB_SIZE / 2,
	},
	trackActive: {
		height: TRACK_HEIGHT,
		borderTopLeftRadius: TRACK_HEIGHT / 2,
		borderBottomLeftRadius: TRACK_HEIGHT / 2,
		borderTopRightRadius: 2,
		borderBottomRightRadius: 2,
		alignItems: "center",
		flexDirection: "row",
		overflow: "hidden",
	},
	trackInactive: {
		height: TRACK_HEIGHT,
		borderTopRightRadius: TRACK_HEIGHT / 2,
		borderBottomRightRadius: TRACK_HEIGHT / 2,
		borderTopLeftRadius: 2,
		borderBottomLeftRadius: 2,
		alignItems: "center",
		justifyContent: "flex-end",
		flexDirection: "row",
		overflow: "hidden",
	},
	thumb: {
		position: "absolute",
		width: 16,
		height: THUMB_SIZE,
		alignItems: "center",
		borderRadius: 6,
		top: -(THUMB_SIZE - TRACK_HEIGHT) / 2,
		left: 0,
		zIndex: 2,
	},
	valueLabel: {
		position: "absolute",
		bottom: THUMB_SIZE + 4,
		minWidth: 48,
		textAlign: "center",
		padding: 12,
		borderRadius: 100,
	},
});
